import { useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  ClipboardCheck,
  GitCompare,
  History,
  Layers,
  Scissors,
} from 'lucide-react';
import { ClusterExplorer } from '@/components/knowledge/resolution/ClusterExplorer';
import { BatchesTab } from '@/components/knowledge/resolution/BatchesTab';
import { EdgeQualityPanel } from '@/components/knowledge/resolution/EdgeQualityPanel';
import { ClusterHistoryTab } from '@/components/knowledge/resolution/ClusterHistoryTab';

type Tab = 'clusters' | 'history' | 'batches' | 'edges';

const TABS: Array<{ id: Tab; label: string; Icon: React.ElementType }> = [
  { id: 'clusters', label: 'Cluster Explorer', Icon: Layers },
  { id: 'history', label: 'Cluster History', Icon: History },
  { id: 'batches', label: 'Resolution Batches', Icon: GitCompare },
  { id: 'edges', label: 'Edge Quality', Icon: Scissors },
];

export function ResolutionPipelinePage() {
  const [tab, setTab] = useState<Tab>('clusters');

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
        <div>
          <h1 className="text-xl font-semibold text-navy-900">Resolution Pipeline</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Cluster discovery, batch audits, and edge quality tools
          </p>
        </div>
        <Link
          to="/knowledge/resolution/review"
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand-teal text-navy-900 font-semibold text-sm rounded-lg hover:bg-brand-teal/90 transition-colors shadow-sm"
        >
          <ClipboardCheck size={14} />
          Review Queue
        </Link>
      </div>

      <div className="flex border-b border-slate-200 bg-white px-6">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors',
              tab === t.id
                ? 'text-brand-teal border-b-2 border-brand-teal'
                : 'text-slate-500 hover:text-slate-700',
            )}
          >
            <t.Icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-auto p-6">
        {tab === 'clusters' && <ClusterExplorer />}
        {tab === 'history' && <ClusterHistoryTab />}
        {tab === 'batches' && <BatchesTab />}
        {tab === 'edges' && <EdgeQualityPanel />}
      </div>
    </div>
  );
}
